<script setup lang="ts">
import ProgressStepsBar from '../progress-steps/ProgressStepsBar.vue';
import ChevronLeftSVG from '@shared/assets/icon/chevron-left.svg';
import { computed } from 'vue';
import type { PropType } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps({
  pageClass: {
    type: String,
    default: '',
    validator: (val: string) =>
      ['', 'section-page', 'subsection-page', 'fullfocus-page'].includes(val),
  },
  withBackArrow: {
    type: Boolean,
    default: false,
  },
  customBackAction: {
    type: Function as PropType<() => void>,
    default: null,
  },
  titleTight: { type: Boolean, default: false },
  backgroundColor: {
    //can overwrite instead of default based on pageClass
    type: String,
    default: null,
    validator: (val: string) => ['bg-dark', 'bg-light', 'bg-white'].includes(val),
  },
  subTitleColor: {
    type: String,
    default: 'primary',
    validator: (val: string) => ['secondary', 'primary'].includes(val),
  },
  /** Extra class(es) on the title back button when `withBackArrow` is true (set from the page). */
  backButtonClass: {
    type: String,
    default: '',
  },
});

const showProgressSteps = computed(() => {
  return props.pageClass !== 'fullfocus-page';
});

const bgColorClass = computed(() => {
  if (props.backgroundColor) {
    return props.backgroundColor;
  }

  let bgClass = '';
  switch (props.pageClass) {
    case 'section-page':
      bgClass = 'bg-dark';
      break;
    case 'subsection-page':
      bgClass = 'bg-light';
      break;
    case 'fullfocus-page':
      bgClass = 'bg-white';
      break;
  }
  return bgClass;
});

const subTitleColorClass = computed(() => {
  if (props.subTitleColor) {
    return 'color-' + props.subTitleColor;
  }
  return undefined;
});

const titleClass = computed(() => {
  return props.titleTight ? ['title', 'title--tight'] : ['title'];
});
const titleTextClass = computed(() => {
  return [
    'title__text',
    props.pageClass === 'section-page' ? 'h2' : 'h3',
    props.withBackArrow ? 'title__text--with-back-arrow' : '',
  ];
});
const backButtonClassList = computed(() => {
  const classes: string[] = ['title__back-button'];
  if (props.backButtonClass) {
    classes.push(props.backButtonClass);
  }
  return classes;
});
const router = useRouter();

const goBack = () => {
  if (props.customBackAction) {
    props.customBackAction();
  } else {
    router.go(-1);
  }
};
</script>

<template>
  <div :class="['progress-frame', bgColorClass]">
    <div v-if="showProgressSteps" class="progress-steps-bar">
      <ProgressStepsBar />
    </div>
    <transition name="page" mode="out-in">
      <main :key="$route.path" class="main">
        <div v-if="$slots.pageIcon" class="page-icon" aria-hidden="true">
          <slot name="pageIcon" />
        </div>

        <div v-if="$slots.title" :class="titleClass">
          <div class="title__flex-wrap">
            <button
              v-if="props.withBackArrow"
              :class="backButtonClassList"
              aria-label="Go back to previous page"
              @click="goBack"
            >
              <ChevronLeftSVG />
            </button>

            <div :class="titleTextClass">
              <slot name="title" />
            </div>
          </div>
        </div>

        <div v-if="$slots.subTitle" class="sub-title">
          <div :class="subTitleColorClass">
            <slot name="subTitle" />
          </div>
        </div>
        <slot />
      </main>
    </transition>
  </div>
</template>

<style lang="less" scoped>
@page-title-back-arrow-width: 2.85rem;

.progress-frame {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.progress-steps-bar {
  display: flex;
  flex-direction: column;
}

.main {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  margin-bottom: @page-bottom-margin;
}

.bg-dark {
  background-color: @color-background-dark;
}

.bg-light {
  background-color: @color-background-light;
}

.bg-white {
  background-color: white;
}

.page-icon {
  margin: 0.6rem @content-side-margin;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: @color-primary;
}

.title {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  position: relative;
  width: 100%;
  height: 100%;
  min-height: 5.14rem;
  text-align: center;
  margin-bottom: 1.28rem;

  &.title--tight {
    min-height: 0;
    margin-bottom: 0.3rem;
  }
}

.title__flex-wrap {
  display: flex;
  justify-content: center;
  margin: 0 @content-side-margin;
}

.title__text {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  height: 100%;
  margin: 0 @content-side-margin;

  &.h2 {
    font-weight: @font-weight-bold;
  }

  &.title__text--with-back-arrow {
    width: 100%;
    margin: 0 @page-title-back-arrow-width;
  }
}

.title__back-button {
  position: absolute;
  left: 0px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;

  // Remove all button-like styling
  background: none;
  border: none;
  padding: 8px;
  margin: 0;
  cursor: pointer;
  outline: none;

  svg {
    color: @color-primary;
    width: @page-title-back-arrow-width;
  }

  &:hover,
  &:focus,
  &:focus-visible {
    background-color: fade(@color-primary, 10%);
    border-radius: 50%;
  }
}

.sub-title {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin: 0 @content-side-margin;

  .color-secondary {
    color: @color-secondary;
  }
  .color-primary {
    color: @color-primary;
  }
}

// transition
.page-enter-active,
.page-leave-active {
  transition: all 0.3s;
}
.page-enter-from {
  transform: translateX(100px);
  opacity: 0;
}
.page-leave-to {
  transform: translateX(-100px);
  opacity: 0;
}
</style>
